/**
 * @fileOverview
 * Adapts the `GET studies/logbook/` payload to the camelCase view model the Studies logbook
 * table binds to. Keeping the wire shape out of the component means a field rename on the
 * backend is a one-line change here rather than a sweep through the template.
 */
import {
  StudyLogEntry,
  StudyLogResponse,
  StudyLogbookApiResponse,
  StudyLogbookApiStudy
} from '../models/study-log.model';

/**
 * Maps one study from the wire shape onto the row the table renders.
 *
 * `study_id`, `workflow` and `last_save_comment` all arrive as null in real data — see the
 * model for when — and the table sorts, filters and searches on these values, so whatever
 * stands in for a null has to be a string the rest of the screen can handle.
 */
export function toStudyLogEntry(study: StudyLogbookApiStudy): StudyLogEntry {
  return {
    studyId: study.study_id ?? '',
    studyName: study.run_name,
    firstSaved: study.first_saved,
    lastSaved: study.last_saved,
    lastSavedBy: study.last_saved_by,
    instruments: study.instruments,
    partsTested: study.parts_tested,
    lotsTested: study.lots_tested,
    panel: study.panel_name,
    workflow: study.workflow ?? '',
    isDraft: study.is_draft,
    isCommitted: study.is_committed,
    comment: study.last_save_comment ?? '',
    resultTypes: study.result_types
  }
}

/** Maps a whole logbook payload, tolerating a response that arrives without either list. */
export function toStudyLogResponse(response: StudyLogbookApiResponse | null): StudyLogResponse {
  return {
    years: response?.years ?? [],
    studies: (response?.studies ?? []).map(toStudyLogEntry)
  };
}
